"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { Home, Star, Heart, ListOrdered, Lock } from "lucide-react";
import { cn } from "@/lib/helpers/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useAuth } from "@/contexts/AuthContext";

export function BottomNav() {
  const pathname = usePathname();
  const { translate } = useLanguage();
  const { favourites } = useFavourites();
  const { preferences } = useUserPreferences();
  const { status } = useAuth();
  // Recommended, Favourites and Priorities are signed-in-only (requirement 3).
  const isSignedIn = status === "authenticated";

  // Calculate counts
  const homeResultsCount = preferences.homeResultsCount ?? (preferences.homeResults?.length || 0);
  const recommendedCount = preferences.recommendedRecommendations?.length || 0;
  const favouritesCount = favourites.length;
  const prioritiesCount = preferences.prioritiesSelectedProgrammes?.length || 0;

  const navigation = [
    { name: translate("home"), href: "/", icon: Home, count: homeResultsCount, gated: false },
    { name: translate("recommended"), href: "/recommended", icon: Star, count: recommendedCount, gated: true },
    { name: translate("favourites"), href: "/favourites", icon: Heart, count: favouritesCount, gated: true },
    { name: translate("priorities"), href: "/priorities", icon: ListOrdered, count: prioritiesCount, gated: true },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 border-t-2 border-rose-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] md:hidden" style={{ background: 'linear-gradient(to top, #ffe4e6, #ffffff)' }}>
      <div className="flex items-center px-2 py-2 gap-1">
        {/* Logo on the left */}
        <div className="flex items-center justify-center px-2">
          <Image 
            src="/images/icon-1.png" 
            alt="Logo" 
            width={42} 
            height={42}
            className="object-contain"
          />
        </div>
        
        {/* Navigation items */}
        <div className="flex items-center justify-around flex-1">
          {navigation.map((item) => {
          const isActive = pathname === item.href;
          const Icon = item.icon;
          const locked = item.gated && !isSignedIn;

          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                "flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg transition-all duration-200 min-w-[70px]",
                isActive
                  ? "text-primary bg-primary/10"
                  : "text-gray-600 hover:text-primary hover:bg-gray-100"
              )}
            >
              <div className="relative">
                <Icon className={cn("h-5 w-5", isActive && "stroke-[2.5]")} />
                {locked ? (
                  <span
                    className="absolute -top-2 -right-2 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-gray-400 text-white"
                    title={translate("featureLoggedInOnly")}
                  >
                    <Lock className="h-2.5 w-2.5" />
                  </span>
                ) : item.count > 0 && (
                  <span className={cn(
                    "absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold px-1",
                    isActive
                      ? "bg-primary text-white"
                      : "bg-gray-400 text-white"
                  )}>
                    {item.count}
                  </span>
                )}
              </div>
              <span className={cn(
                "text-[10px] font-medium text-center leading-tight",
                isActive && "font-semibold"
              )}>
                {item.name}
              </span>
            </Link>
          );
        })}
        </div>
      </div>
    </nav>
  );
}
